import { Fragment, type MouseEvent, type ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import type { MonitoringFilters } from '../hooks/useMonitoringFilters';
import type { AlertSummary } from '../hooks/usePageAlerts';
import { getSecondaryName, hasNumber, type MonitoringRow } from '../monitoringRow';
import { type ColumnKey, type MaintenanceStatus, type SortKey, getMetricAge, isSchedulable } from '../monitoringUtils';
import { MONITORING_COLUMNS } from './columns';
import { getMaintenanceGroupName } from './maintenanceGroupName';
import useFmText from './useFmText';
import type { DetailTab } from './VehicleDetailDrawer';
import './MonitoringTable.scss';

export interface IRowContext {
    getStatus: (row: MonitoringRow) => MaintenanceStatus | undefined;
    hasService: (row: MonitoringRow) => boolean;
    hasVolksTotal: (row: MonitoringRow) => boolean;
    getAccountName: (row: MonitoringRow) => string | undefined;
}

interface IMonitoringTableProps {
    rows: MonitoringRow[];
    visibleColumns: Set<ColumnKey>;
    sort: MonitoringFilters['sort'];
    onSort: (key: SortKey) => void;
    loading: boolean;
    loadingLabel?: string;
    context: IRowContext;
    getAlertSummary: (assetId: string) => AlertSummary | undefined;
    onOpen: (row: MonitoringRow, tab?: DetailTab) => void;
    onSchedule: (row: MonitoringRow) => void;
    onContract: () => void;
    emptyMessage: string;
    emptyState?: ReactNode;
}

export const STATUS_TONE: Record<MaintenanceStatus, string> = {
    agendar: 'fm-info',
    atrasada: 'fm-bad',
    agendada: 'fm-ok',
    manut: 'fm-warn',
};

const SKELETON_ROWS = ['s1', 's2', 's3', 's4', 's5', 's6', 's7', 's8'];

const Skeleton = ({ width = '70%' }: { width?: string | number }) => (
    <span className='fm-skel' style={{ width, height: 12 }} />
);

const MonitoringTable = ({
    rows,
    visibleColumns,
    sort,
    onSort,
    loading,
    loadingLabel,
    context,
    getAlertSummary,
    onOpen,
    onSchedule,
    onContract,
    emptyMessage,
    emptyState,
}: IMonitoringTableProps) => {
    const { t, formatNumber, formatDate, vehicleType, kmToNext, updatedAgo, formatMetric } = useFmText();
    const columns = MONITORING_COLUMNS.filter(column => visibleColumns.has(column.key));
    const headRef = useRef<HTMLDivElement>(null);
    const wrapRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const head = headRef.current;
        if (!head) {
            return;
        }
        let scroller = head.parentElement;
        while (scroller) {
            const { overflowY } = window.getComputedStyle(scroller);
            if (overflowY === 'auto' || overflowY === 'scroll') {
                break;
            }
            scroller = scroller.parentElement;
        }
        const paddingTop = scroller ? Number.parseFloat(window.getComputedStyle(scroller).paddingTop) || 0 : 0;
        head.style.top = `${-paddingTop}px`;
    }, []);

    useEffect(() => {
        if (headRef.current && wrapRef.current) {
            headRef.current.scrollLeft = wrapRef.current.scrollLeft;
        }
    }, [columns.length]);

    const openTab = (row: MonitoringRow, tab: DetailTab) => (event: MouseEvent) => {
        event.stopPropagation();
        onOpen(row, tab);
    };

    const renderAlertIcon = (row: MonitoringRow) => {
        const summary = getAlertSummary(row.id);
        if (!summary?.total) {
            return null;
        }
        const label = summary.critical
            ? t('table.criticalAlertsTooltip', { count: summary.critical })
            : t('table.alertsTooltip', { count: summary.total });
        return (
            <button
                type='button'
                className={`fm-rowicon${summary.critical ? ' fm-bad' : ''}`}
                title={label}
                aria-label={label}
                onClick={openTab(row, 'alerts')}
            >
                <span className='rioglyph rioglyph-warning-sign' />
                <span className='fm-num'>{formatNumber(summary.critical || summary.total)}</span>
            </button>
        );
    };

    const renderReviewsIcon = (row: MonitoringRow) => {
        const label = t('table.viewReviews');
        return (
            <button
                type='button'
                className='fm-rowicon fm-info'
                title={label}
                aria-label={label}
                onClick={openTab(row, 'reviews')}
            >
                <span className='rioglyph rioglyph-info-sign' />
            </button>
        );
    };

    const renderKm = (km?: number) => {
        const label = kmToNext(km);
        return label ? <small className='fm-num'>{label}</small> : null;
    };

    const renderMetric = (row: MonitoringRow, key: 'odometer' | 'hourmeter', unit: string) => {
        if (!row.loaded) {
            return <Skeleton />;
        }
        const metric = row[key];
        if (!metric) {
            return '–';
        }
        const { days, stale } = getMetricAge(key, metric.date);
        return (
            <>
                <b>{formatMetric(metric, unit)}</b>
                {days !== undefined && <small className={stale ? 'fm-stale' : ''}>{updatedAgo(days)}</small>}
            </>
        );
    };

    const renderCell = (key: ColumnKey, row: MonitoringRow): ReactNode => {
        const status = context.getStatus(row);

        switch (key) {
            case 'veic': {
                const secondaryName = getSecondaryName(row);
                return (
                    <td className='fm-veh'>
                        <b>{row.licensePlate ?? row.chassis}</b>
                        {secondaryName && <small> · {secondaryName}</small>}
                        {renderAlertIcon(row)}
                        <span className='fm-mono'>{row.chassis}</span>
                        <small>{vehicleType(row.type)}</small>
                    </td>
                );
            }
            case 'conta': {
                const account = context.getAccountName(row);
                return <td>{account ?? (row.loaded ? '–' : <Skeleton />)}</td>;
            }
            case 'prox': {
                const next = row.nextMaintenance;
                if (!row.loaded && !status) {
                    return (
                        <td>
                            <Skeleton />
                        </td>
                    );
                }
                if (!next?.number && !status) {
                    return (
                        <td>
                            <span className='fm-pill fm-mut'>{t('status.noPlan')}</span>
                        </td>
                    );
                }
                return (
                    <td className='fm-cell2'>
                        {hasNumber(next?.number) && <b>{t('table.reviewNumber', { number: next.number })} </b>}
                        {status && <span className={`fm-pill ${STATUS_TONE[status]}`}>{t(`status.${status}`)}</span>}
                        {renderReviewsIcon(row)}
                        {renderKm(next?.km)}
                    </td>
                );
            }
            case 'ult': {
                if (!row.loaded) {
                    return (
                        <td>
                            <Skeleton />
                        </td>
                    );
                }
                const last = row.lastMaintenance;
                const lastNumber = last?.number;
                if (!hasNumber(lastNumber) && !last?.checkupType && !last?.date) {
                    return <td>–</td>;
                }
                const label = [
                    hasNumber(lastNumber) ? t('table.reviewNumber', { number: lastNumber }) : '',
                    last?.checkupType,
                ]
                    .filter(Boolean)
                    .join(' · ');
                return (
                    <td className='fm-cell2'>
                        {label || '–'}
                        {last?.date && <small>{formatDate(last.date)}</small>}
                    </td>
                );
            }
            case 'odo':
                return <td className='fm-cell2 fm-num'>{renderMetric(row, 'odometer', 'km')}</td>;
            case 'hor':
                return <td className='fm-cell2 fm-num'>{renderMetric(row, 'hourmeter', 'h')}</td>;
            case 'gar': {
                if (!row.loaded) {
                    return (
                        <td>
                            <Skeleton width={60} />
                        </td>
                    );
                }
                if (!row.warranty) {
                    return <td>{row.warrantyRaw || '–'}</td>;
                }
                return (
                    <td>
                        <span className={`fm-pill ${row.warranty === 'in' ? 'fm-ok' : 'fm-mut'}`}>
                            {t(`warranty.${row.warranty}`)}
                        </span>
                    </td>
                );
            }
            case 'svc': {
                const connected = context.hasService(row);
                const volksTotal = context.hasVolksTotal(row);
                return (
                    <td>
                        <div className='fm-svc'>
                            <span className={`fm-pill ${connected ? 'fm-ok' : 'fm-mut'}`}>
                                {connected ? row.connectivity || t('services.connected') : t('services.notConnected')}
                            </span>
                            <span className={`fm-pill ${volksTotal ? 'fm-info' : 'fm-mut'}`}>
                                {volksTotal ? row.volksTotal || 'Volks|Total' : t('services.noVolksTotal')}
                            </span>
                        </div>
                    </td>
                );
            }
            case 'conexao': {
                if (!row.loaded) {
                    return (
                        <td>
                            <Skeleton width={60} />
                        </td>
                    );
                }
                return (
                    <td>
                        <span className={`fm-pill ${row.deviceConnected ? 'fm-ok' : 'fm-mut'}`}>
                            {row.deviceConnected ? t('connection.online') : t('connection.offline')}
                        </span>
                    </td>
                );
            }
            case 'camp': {
                if (!row.loaded) {
                    return (
                        <td>
                            <Skeleton width={40} />
                        </td>
                    );
                }
                return (
                    <td className='fm-num'>
                        {row.campaignCount > 0 ? (
                            <span className='fm-pill fm-warn'>
                                {t('table.pendingCampaigns', { count: row.campaignCount })}
                            </span>
                        ) : (
                            <span className='fm-muted'>–</span>
                        )}
                    </td>
                );
            }
            case 'grupo':
                return (
                    <td>{row.loaded ? getMaintenanceGroupName(t, row.maintenanceGroup) : <Skeleton width={60} />}</td>
                );
            case 'acao': {
                const canSchedule = isSchedulable(status);
                const needsContract = !context.hasService(row) || !context.hasVolksTotal(row);
                return (
                    <td onClick={event => event.stopPropagation()}>
                        {canSchedule ? (
                            <button type='button' className='fm-act' onClick={() => onSchedule(row)}>
                                {t('actions.schedule')}
                            </button>
                        ) : needsContract ? (
                            <button type='button' className='fm-act fm-ghost' onClick={onContract}>
                                {t('actions.contract')}
                            </button>
                        ) : null}
                    </td>
                );
            }
            default:
                return <td />;
        }
    };

    const tableWidth = columns.reduce((total, column) => total + column.width, 0);
    const tableStyle = { width: `max(100%, ${tableWidth}px)` };
    const colgroup = (
        <colgroup>
            {columns.map(column => (
                <col key={column.key} style={{ width: column.width }} />
            ))}
        </colgroup>
    );

    const syncHeader = () => {
        if (headRef.current && wrapRef.current) {
            headRef.current.scrollLeft = wrapRef.current.scrollLeft;
        }
    };

    return (
        <div className='fm-tableblock'>
            {loadingLabel && (
                <output className='fm-loading' aria-live='polite'>
                    <div className='fm-progress'>
                        <i />
                    </div>
                    <span>{loadingLabel}</span>
                </output>
            )}
            <div className='fm-tablehead' ref={headRef}>
                <table className='fm-table' style={tableStyle}>
                    {colgroup}
                    <thead>
                        <tr>
                            {columns.map(({ key, sort: columnSort }) => (
                                <th
                                    key={key}
                                    aria-sort={
                                        columnSort && sort.key === columnSort
                                            ? sort.dir === 1
                                                ? 'ascending'
                                                : 'descending'
                                            : undefined
                                    }
                                >
                                    {columnSort ? (
                                        <button type='button' onClick={() => onSort(columnSort)}>
                                            {t(`columns.${key}`)}
                                            {sort.key === columnSort && <span>{sort.dir === 1 ? '▲' : '▼'}</span>}
                                        </button>
                                    ) : (
                                        t(`columns.${key}`)
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                </table>
            </div>
            <div className='fm-tablewrap' ref={wrapRef} onScroll={syncHeader}>
                <table className='fm-table' style={tableStyle}>
                    {colgroup}
                    <tbody>
                        {loading && !rows.length
                            ? SKELETON_ROWS.map(skeletonKey => (
                                  <tr key={skeletonKey}>
                                      {columns.map(column => (
                                          <td key={column.key}>
                                              <Skeleton />
                                          </td>
                                      ))}
                                  </tr>
                              ))
                            : rows.map(row => (
                                  <tr
                                      key={row.id}
                                      className='fm-row'
                                      tabIndex={0}
                                      onClick={() => onOpen(row)}
                                      onKeyDown={event => {
                                          if (event.key === 'Enter' && event.target === event.currentTarget) {
                                              event.preventDefault();
                                              onOpen(row);
                                          }
                                      }}
                                  >
                                      {columns.map(column => (
                                          <Fragment key={column.key}>{renderCell(column.key, row)}</Fragment>
                                      ))}
                                  </tr>
                              ))}
                        {!loading && !rows.length && (
                            <tr>
                                <td colSpan={columns.length} className='fm-empty'>
                                    {emptyState ?? emptyMessage}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MonitoringTable;
